import { useMemo } from 'react'
import { View, Text, Pressable } from 'react-native'
import type { LandingNavItem, OrganizationMenuItem } from './LandingNewNav'
import { useTheme } from '../../../src/theme/useTheme'
import { getLandingNavMobileMenuStyles } from './LandingNavMobileMenu.styles'

interface MobileMenuTranslations {
  features: { title: string }
  howItWorks: { title: string }
  siteNav: { packs: string; contact: string; signIn: string; signOut: string }
}

interface LandingNavMobileMenuProps {
  onPressFeatures?: () => void
  onPressHowItWorks?: () => void
  onPressPricing?: () => void
  onPressContact?: () => void
  primaryNavItems?: LandingNavItem[]
  organizationMenuItems?: OrganizationMenuItem[]
  hasOrganizationMenu: boolean
  closeMobileMenu: () => void
  goSignIn: () => void
  handleSignOut: () => void
  isLoaded: boolean
  isSignedIn: boolean | null | undefined
  t: MobileMenuTranslations
}

export function LandingNavMobileMenu({
  onPressFeatures,
  onPressHowItWorks,
  onPressPricing,
  onPressContact,
  primaryNavItems,
  organizationMenuItems,
  hasOrganizationMenu,
  closeMobileMenu,
  goSignIn,
  handleSignOut,
  isLoaded,
  isSignedIn,
  t,
}: LandingNavMobileMenuProps) {
  const theme = useTheme()
  const styles = useMemo(() => getLandingNavMobileMenuStyles(theme), [theme])
  const hasPrimaryNavItems = !!primaryNavItems?.length

  return (
    <View style={styles.mobileMenu}>
      {hasPrimaryNavItems
        ? primaryNavItems.map((item) => (
            <Pressable
              key={item.key}
              accessibilityRole="link"
              onPress={() => { item.onPress(); closeMobileMenu() }}
              style={({ pressed }) => [styles.mobileNavLink, pressed && styles.mobileNavLinkPressed]}
            >
              <Text style={styles.mobileNavLinkText}>{item.label}</Text>
            </Pressable>
          ))
        : null}
      {!hasPrimaryNavItems && onPressFeatures ? (
        <Pressable
          accessibilityRole="link"
          onPress={() => { onPressFeatures(); closeMobileMenu() }}
          style={({ pressed }) => [styles.mobileNavLink, pressed && styles.mobileNavLinkPressed]}
        >
          <Text style={styles.mobileNavLinkText}>{t.features.title}</Text>
        </Pressable>
      ) : null}
      {!hasPrimaryNavItems && onPressHowItWorks ? (
        <Pressable
          accessibilityRole="link"
          onPress={() => { onPressHowItWorks(); closeMobileMenu() }}
          style={({ pressed }) => [styles.mobileNavLink, pressed && styles.mobileNavLinkPressed]}
        >
          <Text style={styles.mobileNavLinkText}>{t.howItWorks.title}</Text>
        </Pressable>
      ) : null}
      {!hasPrimaryNavItems && onPressPricing ? (
        <Pressable
          accessibilityRole="link"
          onPress={() => { onPressPricing(); closeMobileMenu() }}
          style={({ pressed }) => [styles.mobileNavLink, pressed && styles.mobileNavLinkPressed]}
        >
          <Text style={styles.mobileNavLinkText}>{t.siteNav.packs}</Text>
        </Pressable>
      ) : null}
      {!hasPrimaryNavItems && hasOrganizationMenu && organizationMenuItems
        ? organizationMenuItems.map((item) => (
            <Pressable
              key={item.key}
              accessibilityRole="link"
              onPress={() => { item.onPress(); closeMobileMenu() }}
              style={({ pressed }) => [styles.mobileNavLink, pressed && styles.mobileNavLinkPressed]}
            >
              <Text style={styles.mobileNavLinkText}>{item.label}</Text>
            </Pressable>
          ))
        : null}
      {!hasPrimaryNavItems && onPressContact ? (
        <Pressable
          accessibilityRole="link"
          onPress={() => { onPressContact(); closeMobileMenu() }}
          style={({ pressed }) => [styles.mobileNavLink, pressed && styles.mobileNavLinkPressed]}
        >
          <Text style={styles.mobileNavLinkText}>{t.siteNav.contact}</Text>
        </Pressable>
      ) : null}
      {isLoaded && !isSignedIn ? (
        <Pressable
          accessibilityRole="link"
          onPress={() => { goSignIn(); closeMobileMenu() }}
          style={({ pressed }) => [styles.mobileNavLink, pressed && styles.mobileNavLinkPressed]}
        >
          <Text style={styles.mobileNavLinkText}>{t.siteNav.signIn}</Text>
        </Pressable>
      ) : null}
      {isLoaded && isSignedIn ? (
        <Pressable
          accessibilityRole="button"
          onPress={() => { handleSignOut(); closeMobileMenu() }}
          style={({ pressed }) => [styles.mobileNavLink, pressed && styles.mobileNavLinkPressed]}
        >
          <Text style={[styles.mobileNavLinkText, styles.mobileNavLinkSignOut]}>{t.siteNav.signOut}</Text>
        </Pressable>
      ) : null}
    </View>
  )
}
